import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Download, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { paraBooleano } from '@/lib/utils';
import type { RespostaPaginada } from '@/components/paginacao';
import type { LocalAtendimento } from '@/features/admin/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { ImportarProfissionaisCnes } from '@/components/admin/importar-profissionais-cnes';
import { BarraBusca } from '@/components/barra-busca';
import { Paginacao } from '@/components/paginacao';
import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { EsqueletoTabela } from '@/components/ui/skeleton';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

const esquema = z.object({
  nome: z.string().min(3, 'Informe o nome do local.'),
  cnes: z.string().optional(),
  endereco: z.string().optional(),
  telefone: z.string().optional(),
  ativo: z.boolean().optional(),
});

type Formulario = z.infer<typeof esquema>;

const VAZIO: Formulario = { nome: '', cnes: '', endereco: '', telefone: '' };

const COLUNAS = 6;

/**
 * Cadastro dos locais de atendimento.
 *
 * A lista só é consultada no agendamento manual — quando o eSUS responde, o
 * local vem da integração. Endereço e telefone ficam aqui para que a Regulação
 * não precise redigitá-los a cada agendamento digitado à mão.
 */
export function PaginaLocais() {
  const queryClient = useQueryClient();
  const [pagina, setPagina] = React.useState(1);
  const [busca, setBusca] = React.useState('');
  const [emEdicao, setEmEdicao] = React.useState<LocalAtendimento | null>(null);
  const [dialogoAberto, setDialogoAberto] = React.useState(false);
  const [importando, setImportando] = React.useState<LocalAtendimento | null>(null);

  const parametros = new URLSearchParams({ pagina: String(pagina), limite: '20' });
  if (busca) parametros.set('busca', busca);

  const { data, isPending } = useQuery({
    queryKey: ['locais', pagina, busca],
    queryFn: () => api.get<RespostaPaginada<LocalAtendimento>>(`/locais?${parametros.toString()}`),
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: VAZIO,
  });

  const abrirCriacao = () => {
    setEmEdicao(null);
    formulario.reset(VAZIO);
    setDialogoAberto(true);
  };

  const abrirEdicao = (local: LocalAtendimento) => {
    setEmEdicao(local);
    formulario.reset({
      nome: local.nome,
      cnes: local.cnes ?? '',
      endereco: local.endereco ?? '',
      telefone: local.telefone ?? '',
      ativo: local.ativo,
    });
    setDialogoAberto(true);
  };

  const salvar = useMutation({
    mutationFn: (dados: Formulario) => {
      const corpo = {
        ...dados,
        cnes: dados.cnes || undefined,
        endereco: dados.endereco || undefined,
        telefone: dados.telefone || undefined,
      };

      return emEdicao
        ? api.patch<LocalAtendimento>(`/locais/${emEdicao.id}`, corpo)
        : api.post<LocalAtendimento>('/locais', { ...corpo, ativo: undefined });
    },
    onSuccess: async () => {
      toast.success(emEdicao ? 'Local atualizado.' : 'Local cadastrado.');
      setDialogoAberto(false);
      await queryClient.invalidateQueries({ queryKey: ['locais'] });
    },
    onError: (erro) => {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.');
    },
  });

  const aoEnviar = formulario.handleSubmit((dados) => salvar.mutate(dados));

  return (
    <>
      <CabecalhoPagina
        titulo="Locais de atendimento"
        descricao="Unidades oferecidas à Regulação quando o agendamento precisa ser preenchido à mão."
        acoes={
          <Botao onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Novo local
          </Botao>
        }
      />

      <div className="mb-3">
        <BarraBusca
          valor={busca}
          aoMudar={(valor) => {
            setBusca(valor);
            setPagina(1);
          }}
          placeholder="Buscar por nome, endereço ou CNES"
        />
      </div>

      <Tabela>
        <TabelaCabecalho>
          <TabelaLinha>
            <TabelaTitulo>Nome</TabelaTitulo>
            <TabelaTitulo>CNES</TabelaTitulo>
            <TabelaTitulo>Endereço</TabelaTitulo>
            <TabelaTitulo>Telefone</TabelaTitulo>
            <TabelaTitulo>Situação</TabelaTitulo>
            <TabelaTitulo className="text-right">Ações</TabelaTitulo>
          </TabelaLinha>
        </TabelaCabecalho>
        <TabelaCorpo>
          {isPending && <EsqueletoTabela colunas={COLUNAS} />}

          {!isPending && data?.dados.length === 0 && (
            <TabelaVazia colunas={COLUNAS}>Nenhum local cadastrado.</TabelaVazia>
          )}

          {data?.dados.map((local) => (
            <TabelaLinha key={local.id}>
              <TabelaCelula rotulo="Nome" className="font-medium">
                {local.nome}
              </TabelaCelula>
              <TabelaCelula rotulo="CNES" className="text-silenciado-texto tabular-nums">
                {local.cnes ?? '—'}
              </TabelaCelula>
              <TabelaCelula rotulo="Endereço" className="text-silenciado-texto">
                {local.endereco ?? '—'}
              </TabelaCelula>
              <TabelaCelula rotulo="Telefone" className="text-silenciado-texto tabular-nums">
                {local.telefone ?? '—'}
              </TabelaCelula>
              <TabelaCelula rotulo="Situação">
                <Etiqueta tom={local.ativo ? 'sucesso' : 'neutro'}>
                  {local.ativo ? 'Ativo' : 'Inativo'}
                </Etiqueta>
              </TabelaCelula>
              <TabelaCelula className="text-right whitespace-nowrap">
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => setImportando(local)}
                  disabled={!local.cnes}
                  title={
                    local.cnes
                      ? `Importar o quadro do CNES ${local.cnes}`
                      : 'Cadastre o código CNES deste local para importar o quadro de profissionais.'
                  }
                  aria-label={`Importar profissionais do CNES para ${local.nome}`}
                >
                  <Download aria-hidden="true" />
                  Importar do CNES
                </Botao>
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => abrirEdicao(local)}
                  aria-label={`Editar ${local.nome}`}
                >
                  <Pencil aria-hidden="true" />
                  Editar
                </Botao>
              </TabelaCelula>
            </TabelaLinha>
          ))}
        </TabelaCorpo>
      </Tabela>

      {data && <Paginacao meta={data.meta} aoMudarPagina={setPagina} />}

      <ImportarProfissionaisCnes local={importando} aoFechar={() => setImportando(null)} />

      <Dialogo open={dialogoAberto} onOpenChange={setDialogoAberto}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>{emEdicao ? 'Editar local' : 'Novo local'}</DialogoTitulo>
            <DialogoDescricao>
              Endereço e telefone acompanham o agendamento manual e chegam ao paciente na
              notificação — vale conferi-los.
            </DialogoDescricao>
          </DialogoCabecalho>

          <form onSubmit={(evento) => void aoEnviar(evento)} noValidate>
            <DialogoCorpo>
              <Campo
                id="local-nome"
                rotulo="Nome"
                obrigatorio
                erro={formulario.formState.errors.nome?.message}
              >
                <Entrada autoFocus {...formulario.register('nome')} />
              </Campo>

              <Campo id="local-endereco" rotulo="Endereço">
                <Entrada
                  placeholder="Rua, número, bairro, município/UF"
                  {...formulario.register('endereco')}
                />
              </Campo>

              <Campo id="local-telefone" rotulo="Telefone">
                <Entrada {...formulario.register('telefone')} />
              </Campo>

              <Campo id="local-cnes" rotulo="CNES" ajuda="Código do estabelecimento, se houver.">
                <Entrada {...formulario.register('cnes')} />
              </Campo>

              {emEdicao && (
                <Campo
                  id="local-ativo"
                  rotulo="Situação"
                  ajuda="Inativar não afeta os agendamentos já registrados."
                >
                  <Selecao {...formulario.register('ativo', { setValueAs: paraBooleano })}>
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </Selecao>
                </Campo>
              )}
            </DialogoCorpo>

            <DialogoRodape>
              <Botao variante="contorno" onClick={() => setDialogoAberto(false)}>
                Cancelar
              </Botao>
              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
                Salvar
              </Botao>
            </DialogoRodape>
          </form>
        </DialogoConteudo>
      </Dialogo>
    </>
  );
}
